import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Bell,
  X,
  UserPlus,
  Wand2,
  CheckCircle2,
  ImageDown,
  Check,
  AlertTriangle
} from 'lucide-react';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIF_ICONS = {
  new_member: { icon: UserPlus, color: 'text-amber-500', bg: 'bg-amber-50' },
  account_approved: { icon: CheckCircle2, color: 'text-green-500', bg: 'bg-green-50' },
  generation_request: { icon: Wand2, color: 'text-blue-500', bg: 'bg-blue-50' },
  generation_ready: { icon: ImageDown, color: 'text-purple-500', bg: 'bg-purple-50' },
  project_alert: { icon: AlertTriangle, color: 'text-red-500', bg: 'bg-red-50' },
};

const timeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "à l'instant";
  if (mins < 60) return `il y a ${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `il y a ${hours}h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days}j`;
};

const NotificationBell = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const dropdownRef = useRef(null);

  const fetchCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, { withCredentials: true });
      setUnreadCount(res.data.count);
    } catch {}
  }, []);

  const fetchNotifications = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/notifications?limit=10`, { withCredentials: true });
      setNotifications(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchCount();
    const interval = setInterval(fetchCount, 15000);
    return () => clearInterval(interval);
  }, [fetchCount]);

  useEffect(() => {
    if (showDropdown) fetchNotifications();
  }, [showDropdown, fetchNotifications]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const markRead = async (notifId) => {
    await axios.put(`${API}/notifications/${notifId}/read`, {}, { withCredentials: true });
    setNotifications(n => n.map(x => x.notif_id === notifId ? {...x, is_read: true} : x));
    setUnreadCount(c => Math.max(0, c - 1));
  };

  const markAllRead = async () => {
    await axios.put(`${API}/notifications/read-all`, {}, { withCredentials: true });
    setNotifications(n => n.map(x => ({...x, is_read: true})));
    setUnreadCount(0);
  };

  const openPanel = () => {
    setShowDropdown(false);
    setShowPanel(true);
  };

  return (
    <>
      {/* Bell button */}
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setShowDropdown(!showDropdown)}
          className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors"
          data-testid="notification-bell"
        >
          <Bell className="w-5 h-5 text-gray-600" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1" data-testid="notif-badge">
              {unreadCount > 99 ? '99+' : unreadCount}
            </span>
          )}
        </button>

        {/* Dropdown */}
        {showDropdown && (
          <div className="absolute right-0 sm:right-0 top-full mt-2 w-[calc(100vw-2rem)] sm:w-96 max-w-96 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden" data-testid="notif-dropdown">
            <div className="flex items-center justify-between p-3 border-b border-gray-100">
              <h3 className="font-medium text-gray-900 text-sm">Notifications</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button onClick={markAllRead} className="text-xs text-blue-600 hover:underline">
                    Tout marquer lu
                  </button>
                )}
              </div>
            </div>
            <div className="max-h-80 overflow-y-auto">
              {notifications.length === 0 ? (
                <div className="py-8 text-center text-gray-400 text-sm">Aucune notification</div>
              ) : (
                notifications.slice(0, 6).map(notif => {
                  const config = NOTIF_ICONS[notif.type] || NOTIF_ICONS.new_member;
                  const Icon = config.icon;
                  return (
                    <div
                      key={notif.notif_id}
                      className={`flex items-start gap-3 p-3 border-b border-gray-50 hover:bg-gray-50 cursor-pointer transition-colors ${!notif.is_read ? 'bg-blue-50/30' : ''}`}
                      onClick={() => !notif.is_read && markRead(notif.notif_id)}
                    >
                      <div className={`w-8 h-8 rounded-full ${config.bg} flex items-center justify-center shrink-0 mt-0.5`}>
                        <Icon className={`w-4 h-4 ${config.color}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm ${!notif.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {notif.title}
                        </p>
                        <p className="text-xs text-gray-500 truncate mt-0.5">{notif.message}</p>
                        <p className="text-[10px] text-gray-400 mt-1">{timeAgo(notif.created_at)}</p>
                      </div>
                      {!notif.is_read && (
                        <div className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-2" />
                      )}
                    </div>
                  );
                })
              )}
            </div>
            {notifications.length > 0 && (
              <div className="p-2 border-t border-gray-100">
                <button
                  onClick={openPanel}
                  className="w-full text-center text-sm text-blue-600 hover:bg-blue-50 rounded-lg py-2 transition-colors"
                  data-testid="view-all-notifs"
                >
                  Voir toutes les notifications
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Full Panel Overlay */}
      {showPanel && (
        <NotificationPanel
          onClose={() => setShowPanel(false)}
          onMarkRead={markRead}
          onMarkAllRead={markAllRead}
        />
      )}
    </>
  );
};

const NotificationPanel = ({ onClose, onMarkRead, onMarkAllRead }) => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/notifications?limit=50`, { withCredentials: true });
        setNotifications(res.data);
      } catch {}
      setLoading(false);
    };
    fetch();
  }, []);

  const handleMarkRead = async (notifId) => {
    await onMarkRead(notifId);
    setNotifications(n => n.map(x => x.notif_id === notifId ? {...x, is_read: true} : x));
  };

  const handleMarkAllRead = async () => {
    await onMarkAllRead();
    setNotifications(n => n.map(x => ({...x, is_read: true})));
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="notif-panel">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/20 backdrop-blur-sm" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-md bg-white shadow-2xl border-l border-gray-200 flex flex-col h-full animate-in slide-in-from-right duration-200">
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={handleMarkAllRead} className="text-xs">
              <Check className="w-3 h-3 mr-1" />
              Tout marquer lu
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full spinner" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <Bell className="w-10 h-10 mb-3 opacity-30" />
              <p className="text-sm">Aucune notification</p>
            </div>
          ) : (
            notifications.map(notif => {
              const config = NOTIF_ICONS[notif.type] || NOTIF_ICONS.new_member;
              const Icon = config.icon;
              return (
                <div
                  key={notif.notif_id}
                  className={`flex items-start gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors ${!notif.is_read ? 'bg-blue-50/20' : ''}`}
                >
                  <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center shrink-0`}>
                    <Icon className={`w-5 h-5 ${config.color}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${!notif.is_read ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                      {notif.title}
                    </p>
                    <p className="text-sm text-gray-500 mt-1">{notif.message}</p>
                    <p className="text-xs text-gray-400 mt-2">{timeAgo(notif.created_at)}</p>
                  </div>
                  {!notif.is_read && (
                    <button
                      onClick={() => handleMarkRead(notif.notif_id)}
                      className="shrink-0 mt-1 w-6 h-6 rounded-full hover:bg-blue-100 flex items-center justify-center"
                      title="Marquer comme lu"
                    >
                      <div className="w-2 h-2 bg-blue-500 rounded-full" />
                    </button>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export { NotificationBell };
